import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Table,
  Spinner,
  Modal,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchWarehousePackingById } from "slices/Warehouse/Packing/PackingDone/thunk";
import { MecordColors } from "Constants/Color";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import LoadingSpinner from "components/LoadingSpinner";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import CancelButton from "Common/Components/Buttons/CancelButton";
import InfoIcon from "@mui/icons-material/Info";
import DOMPurify from "dompurify";

const WarehousePackingViewDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
  const [additionalDetailsContent, setAdditionalDetailsContent] = useState("");
  const [additionalDetailsServiceName, setAdditionalDetailsServiceName] = useState("");

  const { currentPacking, loading, error } = useSelector(
    (state: any) => state.PackingDone,
  );

  // Extract the actual data
  const packingRecord = currentPacking?.data || currentPacking || {};
  const soDetails = packingRecord.sales_order_product || {};
  const customer = soDetails.customer_details || {};
  const primaryContact = customer.contact_details?.[0] || {};
  const billTo = soDetails.bill_to || {};
  const billFrom = soDetails.bill_from || {};
  const products = packingRecord.products || [];
  const packedQuantity = packingRecord.packed_quantity || 0;

  useEffect(() => {
    if (id) {
      dispatch(fetchWarehousePackingById(id));
    }
  }, [dispatch, id]);

  const handleViewModal = (item: any) => {
    // Keep the item as-is; serial_numbers may be an array of objects
    setSelectedItem(item);
    setShowModal(true);
  };

  // Helper to format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      })
      .replace(/ /g, "-");
  };

  return (
    <PageContainer>
      <style>
        {`
          .td-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}
      </style>
      <Card className="shadow-none border-0">
        <Card.Header>
          {/* Header */}
          <PageBreadcrumb
            title="View Details"
            titleLink="/warehouse-packing"
            breadcrumbs={[
              { label: "Packing Done", to: "/warehouse-packing" },
              { label: "View Details", to: "" },
            ]}
            backNavigationPath={-1}
          />
          <hr />
        </Card.Header>

        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center text-danger py-5">
            <h5>{error}</h5>
          </div>
        ) : (
          <Card.Body>
            {/* Sales Order Details */}
            <h5 className="fw-bold mb-3">Sales Order Details</h5>
            <div className="mb-4">
              <Row>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Packing No. :
                    </Col>
                    <Col sm={8}>{packingRecord.packing_number || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Packing Date :
                    </Col>
                    <Col sm={8}>{formatDate(packingRecord.packing_date)}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Valid Until Date :
                    </Col>
                    <Col sm={8}>{formatDate(soDetails.valid_until_date)}</Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Initiated By :
                    </Col>
                    <Col sm={8}>{packingRecord.initiated_by || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      SO No. :
                    </Col>
                    <Col sm={8}>{soDetails.sales_order_number}</Col>
                  </Row>
                  <Row className="mb-2 mt-2">
                    <Col sm={4} className="fw-bold">
                      Tags :
                    </Col>
                    <Col sm={8}>
                      {Array.isArray(packingRecord?.tags) && packingRecord.tags.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {packingRecord.tags.map((tag: string) => (
                            <span
                              key={tag}
                              className="badge bg-light text-dark d-flex align-items-center rounded-2"
                              style={{
                                border: "1px solid #dee2e6",
                                padding: "4px 8px",
                                fontSize: "11px",
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            {/* Company details */}
            <h5 className="fw-bold mb-3">Company details</h5>
            <div
              className="p-4 rounded-3 mb-4"
              style={{ backgroundColor: MecordColors.ActiveBackground }}
            >
              <Row>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Company Name :
                    </Col>
                    <Col sm={8}>{customer.company_name || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Company Email :
                    </Col>
                    <Col sm={8}>{customer.email || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Contact Name :
                    </Col>
                    <Col sm={8}>{primaryContact.name || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Phone Number :
                    </Col>
                    <Col sm={8}>{primaryContact.phone_number || "-"}</Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Industry :
                    </Col>
                    <Col sm={8}>{customer.industry || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Company Phone No. :
                    </Col>
                    <Col sm={8}>{customer.phone_number || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Email :
                    </Col>
                    <Col sm={8}>{primaryContact.email || "-"}</Col>
                  </Row>
                </Col>
              </Row>
            </div>

            {/* Billing & Shipping Details */}
            <h5 className="fw-bold mb-3">Billing & Shipping Details</h5>
            <div
              className="p-4 rounded-3 mb-4"
              style={{ backgroundColor: MecordColors.ActiveBackground }}
            >
              <Row>
                <Col md={6}>
                  <Row>
                    <Col sm={3} className="fw-bold">
                      Bill From :
                    </Col>
                    <Col sm={9}>
                      <div>{billFrom.org_address}</div>
                      <div>
                        {billFrom.org_city}, {billFrom.org_state},{" "}
                        {billFrom.org_country} - {billFrom.org_pincode}
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col sm={3} className="fw-bold">
                      Bill To :
                    </Col>
                    <Col sm={9}>
                      <div>{billTo.address}</div>
                      <div>
                        {billTo.city}, {billTo.state}, {billTo.country} -{" "}
                        {billTo.pincode}
                      </div>
                      <div>Phone: +91-{billTo.phone_number}</div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            {/* Product Details */}
            <h5 className="fw-bold mb-3">Product Details</h5>
            <div
              className="mb-4"
              style={{ backgroundColor: MecordColors.ActiveBackground }}
            >
              <Table
                responsive
                className="align-middle text-center mb-0"
                style={{ borderSpacing: "0 8px" }}
              >
                <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                  <tr>
                    <th style={{ minWidth: "60px" }}>S.No</th>
                    <th style={{ minWidth: "140px" }}>Products</th>
                    <th style={{ minWidth: "140px" }}>Product Code</th>
                    <th style={{ minWidth: "140px" }}>Category</th>
                    <th style={{ minWidth: "160px" }}>Sub-Category</th>
                    <th style={{ minWidth: "120px" }}>Make</th>
                    <th style={{ minWidth: "140px" }}>Model</th>
                    <th style={{ minWidth: "130px" }}>Order Quantity</th>
                    <th style={{ minWidth: "140px" }}>Packed Quantity</th>
                    <th style={{ minWidth: "150px" }}>Balance Quantity</th>
                    <th style={{ minWidth: "150px" }}>Additional Details</th>
                    <th style={{ minWidth: "250px" }}>Shipping Address</th>
                    <th
                      style={{
                        minWidth: "100px",
                        position: "sticky",
                        right: 0,
                        background: MecordColors.LightGrayColor,
                        zIndex: 2,
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? (
                    products.map((item: any, index: number) => {
                      return (
                        <tr
                          key={item.sales_order_product_item_id || index}
                          style={{
                            backgroundColor: "#fff",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <td>{index + 1}</td>
                          <td className="text-start">
                            <div className="fw-bold">{item.product_name}</div>
                            <div className="text-muted small">
                              SKU: {item.product_sku || "-"}
                            </div>
                            <div className="text-muted small">
                              HSN: {item.product_hsn || "-"}
                            </div>
                          </td>
                          <td>
                            {item.product_code || item.product_sku || "-"}
                          </td>
                          <td>{item.category_name || "-"}</td>
                          <td>{item.sub_category_name || "-"}</td>
                          <td>{item.make_name || "-"}</td>
                          <td>{item.model_name || "-"}</td>
                          <td>{item.ordered_quantity || 0}</td>
                          <td>{item.current_packed_qty || 0}</td>
                          <td>{item.balance_qty || 0}</td>
                          <td>
                            <div className="text-center">
                              {item.additional_details && (
                                <InfoIcon
                                  style={{
                                    cursor: "pointer",
                                    color: MecordColors.ThemeBGColor,
                                    fontSize: "18px",
                                  }}
                                  onClick={() => {
                                    setAdditionalDetailsContent(item.additional_details || "");
                                    setAdditionalDetailsServiceName(item.product_name || "");
                                    setShowAdditionalDetailsModal(true);
                                  }}
                                />
                              )}
                              {!item.additional_details && "-"}
                            </div>
                          </td>
                          <td>
                            {item.shipping_address ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    {item.shipping_address.address}
                                    <br />
                                    {item.shipping_address.city},{" "}
                                    {item.shipping_address.state}
                                    <br />
                                    {item.shipping_address.country} -{" "}
                                    {item.shipping_address.pin_code}
                                    <br />
                                    Ph: {item.shipping_address.phone_number}
                                  </Tooltip>
                                }
                              >
                                <div className="td-clamp-3">
                                  {item.shipping_address.address}
                                  <br />
                                  {item.shipping_address.city},{" "}
                                  {item.shipping_address.state}
                                  <br />
                                  {item.shipping_address.country} -{" "}
                                  {item.shipping_address.pin_code}
                                  <br />
                                  Ph: {item.shipping_address.phone_number}
                                </div>
                              </OverlayTrigger>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            style={{
                              minWidth: "100px",
                              position: "sticky",
                              right: 0,
                              background: "white",
                              zIndex: 2,
                            }}
                          >
                            <button
                              className="btn btn-sm"
                              style={{
                                backgroundColor: MecordColors?.ThemeBGColor,
                                color: "#FFF",
                              }}
                              onClick={() => handleViewModal(item)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={11} className="text-center py-4">
                        No Products Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Notes and Attachments */}
            <div className="mb-4">
              <Table bordered className="mb-0 align-middle">
                <tbody>
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td className="fw-bold" style={{ width: "200px" }}>
                      Notes
                    </td>
                    <td style={{ whiteSpace: "pre-wrap" }}>{packingRecord.notes || "-"}</td>
                  </tr>
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td className="fw-bold">Attachment</td>
                    <td>
                      {packingRecord.attachments &&
                      packingRecord.attachments.length > 0 ? (
                        packingRecord.attachments.map(
                          (att: string, i: number) => {
                            const parts = att.split("/");
                            const filename = parts[parts.length - 1];
                            return (
                              <div key={i}>
                                <a
                                  href={att}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {filename}
                                </a>
                              </div>
                            );
                          },
                        )
                      ) : (
                        <span className="text-muted">No attachments</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
              <CancelButton
                showConfirmation={false}
                onClick={() => navigate(-1)}
              />
            </div>

            {/* Serial Number View Modal */}
            <Modal
              show={showModal}
              onHide={() => setShowModal(false)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title className="fw-bold">Packing Details</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedItem && (
                  <>
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3">Product Details</h6>
                      <Table
                        bordered
                        responsive
                        className="text-center align-middle"
                      >
                        <thead
                          style={{
                            backgroundColor: MecordColors.LightGrayColor,
                          }}
                        >
                          <tr>
                            <th>Products</th>
                            <th>Product Code</th>
                            <th>Make</th>
                            <th>Model</th>
                            <th>Order Qty</th>
                            <th>Packed Qty</th>
                            <th>Balance Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="text-start">
                              <div>{selectedItem.product_name}</div>
                              <div
                                className="text-muted d-flex flex-column gap-0"
                                style={{ fontSize: "0.7rem" }}
                              >
                                <div>
                                  <span className="fw-semibold">SKU:</span>{" "}
                                  {selectedItem.product_sku}
                                </div>
                                <div>
                                  <span className="fw-semibold">HSN Code:</span>{" "}
                                  {selectedItem.product_hsn}
                                </div>
                              </div>
                            </td>
                            <td>
                              {selectedItem.product_code ||
                                selectedItem.product_sku}
                            </td>
                            <td>{selectedItem.make_name}</td>
                            <td>{selectedItem.model_name}</td>
                            <td>{selectedItem.ordered_quantity || 0}</td>
                            <td>{selectedItem.current_packed_qty || 0}</td>
                            <td>{selectedItem.balance_qty || 0}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    <div>
                      <h6 className="fw-bold mb-3">Serial Numbers</h6>
                      {selectedItem.serial_numbers &&
                      selectedItem.serial_numbers.length > 0 ? (
                        <Table
                          bordered
                          hover
                          responsive
                          className="text-center align-middle mb-0"
                        >
                          <thead
                            style={{
                              backgroundColor: MecordColors.LightGrayColor,
                            }}
                          >
                            <tr>
                              <th style={{ width: "100px" }}>Sr. No.</th>
                              <th>Serial Number</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedItem.serial_numbers.map(
                              (sn: any, idx: number) => {
                                const serialLabel =
                                  typeof sn === "string"
                                    ? sn
                                    : sn.serial_number ||
                                      sn.serial_no ||
                                      sn.serialNo ||
                                      "";
                                return (
                                  <tr
                                    key={idx}
                                    style={{ backgroundColor: "#fff" }}
                                  >
                                    <td>{idx + 1}</td>
                                    <td>{serialLabel}</td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="p-3 border rounded bg-light text-center text-muted">
                          No serial numbers packed for this item.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <CancelButton
                  showConfirmation={false}
                  onClick={() => setShowModal(false)}
                />
              </Modal.Footer>
            </Modal>

            {/* Additional Details View Modal */}
            <Modal
              show={showAdditionalDetailsModal}
              onHide={() => setShowAdditionalDetailsModal(false)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title className="fw-bold">
                  Additional Details - {additionalDetailsServiceName}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ minHeight: "200px" }}>
                <div
                  className="p-3 border rounded bg-light"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(additionalDetailsContent),
                  }}
                />
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <CancelButton
                  onClick={() => setShowAdditionalDetailsModal(false)}
                  showConfirmation={false}
                />
              </Modal.Footer>
            </Modal>
          </Card.Body>
        )}
      </Card>
    </PageContainer>
  );
};

export default WarehousePackingViewDetails;
